<template>
  <div class="flex flex-col min-h-screen">
    <main class="flex-1 py-12 md:py-24 lg:py-32">
      <div class="container px-4 md:px-6 mx-auto max-w-4xl">
        <!-- Header -->
        <div class="mb-8">
          <h1 class="text-4xl font-heading font-bold tracking-tighter sm:text-5xl text-graphite dark:text-white mb-4">
            {{ $t('privacy.title') }}
          </h1>
          <p class="text-sm font-body text-graphite dark:text-gray-light">
            {{ $t('privacy.lastUpdated') }}: {{ $t('privacy.updateDate') }}
          </p>
        </div>

        <!-- Content -->
        <div class="prose prose-lg dark:prose-invert max-w-none space-y-8">
          <!-- Introducción -->
          <section class="space-y-4">
            <p class="font-body text-graphite dark:text-gray-light leading-relaxed">
              {{ $t('privacy.introduction') }}
            </p>
          </section>

          <!-- Información que recopilamos -->
          <section class="space-y-4">
            <h2 class="text-2xl font-heading font-semibold text-graphite dark:text-white mt-8 mb-4">
              {{ $t('privacy.sections.dataCollection.title') }}
            </h2>
            <p class="font-body text-graphite dark:text-gray-light leading-relaxed">
              {{ $t('privacy.sections.dataCollection.description') }}
            </p>
            <ul class="list-disc list-inside space-y-2 font-body text-graphite dark:text-gray-light ml-4">
              <li v-for="(item, index) in dataCollectionItems" :key="index">
                {{ item }}
              </li>
            </ul>
          </section>

          <!-- Cómo usamos la información -->
          <section class="space-y-4">
            <h2 class="text-2xl font-heading font-semibold text-graphite dark:text-white mt-8 mb-4">
              {{ $t('privacy.sections.dataUsage.title') }}
            </h2>
            <p class="font-body text-graphite dark:text-gray-light leading-relaxed">
              {{ $t('privacy.sections.dataUsage.description') }}
            </p>
            <ul class="list-disc list-inside space-y-2 font-body text-graphite dark:text-gray-light ml-4">
              <li v-for="(item, index) in dataUsageItems" :key="index">
                {{ item }}
              </li>
            </ul>
          </section>

          <!-- Cookies -->
          <section class="space-y-4">
            <h2 class="text-2xl font-heading font-semibold text-graphite dark:text-white mt-8 mb-4">
              {{ $t('privacy.sections.cookies.title') }}
            </h2>
            <p class="font-body text-graphite dark:text-gray-light leading-relaxed">
              {{ $t('privacy.sections.cookies.description') }}
            </p>
            <div class="bg-gray-light dark:bg-graphite-light p-4 rounded-lg mt-4">
              <h3 class="text-lg font-heading font-semibold text-graphite dark:text-white mb-2">
                {{ $t('privacy.sections.cookies.types.title') }}
              </h3>
              <ul class="list-disc list-inside space-y-2 font-body text-graphite dark:text-gray-light ml-4">
                <li v-for="(item, index) in cookieTypes" :key="index">
                  {{ item }}
                </li>
              </ul>
            </div>
          </section>

          <!-- Google AdSense -->
          <section class="space-y-4">
            <h2 class="text-2xl font-heading font-semibold text-graphite dark:text-white mt-8 mb-4">
              {{ $t('privacy.sections.adsense.title') }}
            </h2>
            <p class="font-body text-graphite dark:text-gray-light leading-relaxed">
              {{ $t('privacy.sections.adsense.description') }}
            </p>
            <p class="font-body text-graphite dark:text-gray-light leading-relaxed">
              {{ $t('privacy.sections.adsense.optOut') }}
            </p>
            <p class="font-body text-graphite dark:text-gray-light leading-relaxed">
              <a 
                href="https://policies.google.com/privacy" 
                target="_blank" 
                rel="noopener noreferrer"
                class="text-primary hover:underline"
              >
                {{ $t('privacy.sections.adsense.policyLink') }}
              </a>
            </p>
          </section>

          <!-- Compartir información -->
          <section class="space-y-4">
            <h2 class="text-2xl font-heading font-semibold text-graphite dark:text-white mt-8 mb-4">
              {{ $t('privacy.sections.sharing.title') }}
            </h2>
            <p class="font-body text-graphite dark:text-gray-light leading-relaxed">
              {{ $t('privacy.sections.sharing.description') }}
            </p>
            <ul class="list-disc list-inside space-y-2 font-body text-graphite dark:text-gray-light ml-4">
              <li v-for="(item, index) in sharingItems" :key="index">
                {{ item }}
              </li>
            </ul>
          </section>

          <!-- Seguridad -->
          <section class="space-y-4">
            <h2 class="text-2xl font-heading font-semibold text-graphite dark:text-white mt-8 mb-4">
              {{ $t('privacy.sections.security.title') }}
            </h2>
            <p class="font-body text-graphite dark:text-gray-light leading-relaxed">
              {{ $t('privacy.sections.security.description') }}
            </p>
          </section>

          <!-- Tus derechos -->
          <section class="space-y-4">
            <h2 class="text-2xl font-heading font-semibold text-graphite dark:text-white mt-8 mb-4">
              {{ $t('privacy.sections.rights.title') }}
            </h2>
            <p class="font-body text-graphite dark:text-gray-light leading-relaxed">
              {{ $t('privacy.sections.rights.description') }}
            </p>
            <ul class="list-disc list-inside space-y-2 font-body text-graphite dark:text-gray-light ml-4">
              <li v-for="(item, index) in rightsItems" :key="index">
                {{ item }}
              </li>
            </ul>
          </section>

          <!-- Cambios a esta política -->
          <section class="space-y-4">
            <h2 class="text-2xl font-heading font-semibold text-graphite dark:text-white mt-8 mb-4">
              {{ $t('privacy.sections.changes.title') }}
            </h2>
            <p class="font-body text-graphite dark:text-gray-light leading-relaxed">
              {{ $t('privacy.sections.changes.description') }}
            </p>
          </section>

          <!-- Contacto -->
          <section class="space-y-4">
            <h2 class="text-2xl font-heading font-semibold text-graphite dark:text-white mt-8 mb-4">
              {{ $t('privacy.sections.contact.title') }}
            </h2>
            <p class="font-body text-graphite dark:text-gray-light leading-relaxed">
              {{ $t('privacy.sections.contact.description') }}
            </p>
          </section>
        </div>

        <!-- Back to home button -->
        <div class="mt-12 pt-8 border-t border-gray-light dark:border-graphite-light">
          <router-link 
            to="/" 
            class="inline-flex items-center px-6 py-3 bg-primary hover:bg-primary-dark text-graphite font-body font-medium rounded-md transition-colors"
          >
            {{ $t('privacy.backToHome') }}
          </router-link>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const dataCollectionItems = computed(() => {
  const items = t('privacy.sections.dataCollection.items')
  return Array.isArray(items) ? items : []
})

const dataUsageItems = computed(() => {
  const items = t('privacy.sections.dataUsage.items')
  return Array.isArray(items) ? items : []
})

const cookieTypes = computed(() => {
  const items = t('privacy.sections.cookies.types.items')
  return Array.isArray(items) ? items : []
})

const sharingItems = computed(() => {
  const items = t('privacy.sections.sharing.items')
  return Array.isArray(items) ? items : []
})

const rightsItems = computed(() => {
  const items = t('privacy.sections.rights.items')
  return Array.isArray(items) ? items : []
})
</script>

<style scoped>
.container {
  max-width: 1200px;
}

.prose {
  color: inherit;
}

.prose h2 {
  margin-top: 2rem;
  margin-bottom: 1rem;
}

.prose ul {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.prose li {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}
</style>

